import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Save, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { api } from '../utils/api';

interface EmailConfigForm {
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  sender_name: string;
  sender_email: string;
}

const defaultForm: EmailConfigForm = {
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_use_tls: true,
  sender_name: 'MYAIGC',
  sender_email: '',
};

export default function EmailConfig() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [form, setForm] = useState<EmailConfigForm>(defaultForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadConfig();
  }, [user, navigate]);

  const loadConfig = async () => {
    try {
      const response = await api.getEmailConfig();
      setIsConfigured(response.data.configured);
      if (response.data.configured && response.data.config) {
        const config = response.data.config;
        setForm({
          smtp_host: config.smtp_host || '',
          smtp_port: config.smtp_port || 587,
          smtp_user: config.smtp_user || '',
          smtp_password: '', // 密码不回显
          smtp_use_tls: config.smtp_use_tls !== false,
          sender_name: config.sender_name || 'MYAIGC',
          sender_email: config.sender_email || '',
        });
      }
    } catch (error: any) {
      console.error('Failed to load email config:', error);
    }
  };

  const handleSave = async () => {
    if (!form.smtp_host.trim() || !form.smtp_user.trim()) {
      setMessage({ type: 'error', text: t('emailConfig.errors.smtpRequired') });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await api.updateEmailConfig(form);
      if (response.data.success) {
        setIsConfigured(true);
        setMessage({ type: 'success', text: t('emailConfig.success.saved') });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || t('emailConfig.errors.saveFailed'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) {
      setMessage({ type: 'error', text: t('emailConfig.errors.testEmailRequired') });
      return;
    }

    setIsTesting(true);
    setMessage(null);

    try {
      const response = await api.testEmailConfig(testEmail.trim());
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || t('emailConfig.errors.testSendFailed'),
      });
    } finally {
      setIsTesting(false);
    }
  };

  const updateField = (field: keyof EmailConfigForm, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
          <div className="flex items-center space-x-3">
            <Mail className="w-6 h-6 text-white" />
            <div>
              <h1 className="text-xl font-bold text-white">{t('emailConfig.title')}</h1>
              <p className="text-indigo-100 text-sm">{t('emailConfig.description')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* 说明卡片 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800 space-y-2">
                <p><strong>{t('emailConfig.info.title')}</strong></p>
                <p>{t('emailConfig.info.desc')}</p>
              </div>
            </div>
          </div>

          {/* 当前状态 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              {isConfigured ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">
                    {t('emailConfig.currentStatus')}<span className="font-medium text-green-600">{tCommon('status.configured')}</span>
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-700">
                    {t('emailConfig.currentStatus')}<span className="font-medium text-gray-500">{tCommon('status.notConfigured')}</span>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* 配置表单 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('emailConfig.smtpSettings.title')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('emailConfig.smtpSettings.hostLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.smtp_host}
                  onChange={(e) => updateField('smtp_host', e.target.value)}
                  placeholder={t('emailConfig.smtpSettings.hostPlaceholder')}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('emailConfig.smtpSettings.portLabel')}
                </label>
                <input
                  type="number"
                  value={form.smtp_port}
                  onChange={(e) => updateField('smtp_port', parseInt(e.target.value) || 587)}
                  placeholder={t('emailConfig.smtpSettings.portPlaceholder')}
                  className="input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('emailConfig.smtpSettings.userLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.smtp_user}
                  onChange={(e) => updateField('smtp_user', e.target.value)}
                  placeholder={t('emailConfig.smtpSettings.userPlaceholder')}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('emailConfig.smtpSettings.passwordLabel')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.smtp_password}
                    onChange={(e) => updateField('smtp_password', e.target.value)}
                    placeholder={isConfigured ? t('emailConfig.smtpSettings.passwordKeep') : t('emailConfig.smtpSettings.passwordPlaceholder')}
                    className="input pr-20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? t('emailConfig.smtpSettings.hidePassword') : t('emailConfig.smtpSettings.showPassword')}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="smtp_use_tls"
                checked={form.smtp_use_tls}
                onChange={(e) => updateField('smtp_use_tls', e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <label htmlFor="smtp_use_tls" className="text-sm text-gray-700">
                {t('emailConfig.smtpSettings.tlsLabel')}
              </label>
            </div>

            <h3 className="text-lg font-medium text-gray-900 pt-2">{t('emailConfig.senderSettings.title')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('emailConfig.senderSettings.nameLabel')}
                </label>
                <input
                  type="text"
                  value={form.sender_name}
                  onChange={(e) => updateField('sender_name', e.target.value)}
                  placeholder={t('emailConfig.senderSettings.namePlaceholder')}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('emailConfig.senderSettings.emailLabel')}
                </label>
                <input
                  type="text"
                  value={form.sender_email}
                  onChange={(e) => updateField('sender_email', e.target.value)}
                  placeholder={t('emailConfig.senderSettings.emailPlaceholder')}
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center space-x-4 pt-2">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center space-x-2 px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('emailConfig.actions.saving')}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{t('emailConfig.actions.save')}</span>
                </>
              )}
            </button>
          </div>

          {/* 测试邮件 */}
          {isConfigured && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('emailConfig.test.title')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('emailConfig.test.description')}
              </p>
              <div className="flex items-center space-x-4">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder={t('emailConfig.test.placeholder')}
                  className="input flex-1"
                />
                <button
                  onClick={handleTest}
                  disabled={isTesting || !testEmail.trim()}
                  className="flex items-center space-x-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isTesting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('emailConfig.test.sending')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>{t('emailConfig.test.button')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* 消息提示 */}
          {message && (
            <div
              className={`p-4 rounded-lg flex items-start space-x-3 ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* 常见 SMTP 配置 */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('emailConfig.reference.title')}</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-800">{t('emailConfig.reference.qq.name')}</p>
                <p>{t('emailConfig.reference.qq.server')}</p>
                <p>{t('emailConfig.reference.qq.password')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-800">{t('emailConfig.reference.netease.name')}</p>
                <p>{t('emailConfig.reference.netease.server')}</p>
                <p>{t('emailConfig.reference.netease.password')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-800">{t('emailConfig.reference.gmail.name')}</p>
                <p>{t('emailConfig.reference.gmail.server')}</p>
                <p>{t('emailConfig.reference.gmail.password')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
